import type { App } from 'vue'
import { emitError } from '@/lib/observability/events'

/**
 * Captures unhandled promise rejections + (in production) suppresses
 * the noisy browser-console default handler.
 *
 * Two concerns split:
 *
 *  1. **Observability** — every unhandled rejection is pushed to Faro
 *     with the original reason so the stack trace is preserved. Runs
 *     in BOTH dev and prod; the observability latch (see
 *     `lib/observability/events.ts`) decides whether to actually ship
 *     to Loki. Dev devs see the stack in the console anyway; prod
 *     ops see it in dashboards.
 *
 *  2. **UX silence in prod** — the default browser handler logs
 *     rejection objects to the devtools console as red errors,
 *     including the user-visible devtools. `event.preventDefault()`
 *     hides that noise; the underlying rejection is still observed.
 *
 * Faro's built-in `ErrorsInstrumentation` ALSO listens for
 * `unhandledrejection` and pushes errors itself — but it only
 * captures the rejection's stringification, not a full Error stack
 * when the rejection reason is a non-Error. The explicit push below
 * adds context fields (`source`) and a defensive `instanceof Error`
 * wrap so any kind of rejection (string, plain object, undefined,
 * Error) gets a usable stack frame in Loki.
 */
export default {
  install: (_app: App) => {
    window.addEventListener('unhandledrejection', (event) => {
      emitError(event.reason, () => ({ source: 'unhandledrejection' }))
      if (import.meta.env.MODE === 'production') {
        event.preventDefault()
      }
    })
  },
}
